import { useEffect, useState } from 'react';
import { AlertService } from '../../../../services/alert.service';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dropzone } from '@dropzone-ui/react';
import { CampaignsService } from '../../../../services/campaings.service';
import { separatorList } from '../constants/data.constants';
import { AxiosProgressEvent } from 'axios';
import Select from 'react-select';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import Icon from '../../../../components/forms/icon.component';
import Title from '../../../../components/forms/titles.component';
import Switch from '../../../../components/forms/switch.component';
import Tooltip from '../../../../components/forms/tooltip.component';

function Stepper2({ onValidationChange }: { onValidationChange: (isValid: boolean) => void }) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<any[]>([]);
  const [loadingFile, setLoadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [baseKey, setBaseKey] = useState('');

  const { register, control, watch, setValue } = useFormContext();

  const separators = watch('separator');
  const stepper2Valid = !!separators && files.length > 0;

  const updateFiles = async (incomingFiles: any[]) => {
    const allowedExtensions = ['.zip', '.txt', '.csv', '.text'];
    const file = incomingFiles[0];
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    const isAllowed = allowedExtensions.includes(fileExtension);
    const fileSizeMB = file.size / (1024 * 1024);
    const isTooLarge = fileSizeMB > 1024;
    const data = new FormData();
    setValue('keyBase', '');
    setValue('channel', '');
    switch (true) {
      case allowedExtensions.includes(fileExtension) && isTooLarge:
        return AlertService.toastInfo(
          '¡ Atención !, el archivo es demasiado grande. Máximo: 1GB. Te recomendamos comprimir tu archivo en un archivo .zip'
        );

      case fileExtension === '.zip' && isTooLarge:
        return AlertService.toastInfo(
          '¡ Atención !, el archivo ZIP es demasiado grande. Máximo permitido: 1GB'
        );

      case !isAllowed:
        return AlertService.toastError(
          '¡ Atención !, solo se permiten archivos con extensión .zip, .txt, .text o .csv'
        );

      case !separators:
        return AlertService.toastInfo('¡ Atención !, por favor selecciona un tipo de separador.');
    }

    setFiles([file]);
    data.append('file', incomingFiles[0].file);
    data.append('separator', watch('separator'));

    const promise = async () => {
      setLoadingFile(true);
      try {
        const response = await CampaignsService.getHeaders(
          data,
          (progressEvent: AxiosProgressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            setUploadProgress(percent);
          }
        );

        const options = response.headers.map((header: string) => ({
          value: header,
          label: header
        }));

        const examples = Object.entries(response.example || {}).map(([header, value]) => ({
          header,
          value: String(value)
        }));

        setBaseKey(response.key);
        setValue('headersOptions', examples);
        setValue('headersSelects', options);
        setUploadProgress(0);
      } catch (error: any) {
        setFiles([]);
        console.error('Error al obtener headers:', error);
        if (error?.error) {
          AlertService.toastError(error.error);
        } else if (typeof error === 'string') {
          AlertService.toastError(error);
        } else if (error?.status) {
          AlertService.toastError(error.status);
        } else {
          AlertService.toastError('¡ Atencíon !, tu archivo contiene un error por favor revisalo.');
        }
      } finally {
        setLoadingFile(false);
      }
    };

    promise();
  };

  const removeFile = (e: any) => {
    e.stopPropagation();
    setFiles([]);
    setValue('keyBase', '');
    setValue('headers', '');
    setValue('channel', '');
  };

  useEffect(() => {
    setValue('keyBase', baseKey);
  }, [baseKey]);

  useEffect(() => {
    if (files.length > 0) {
      updateFiles(files);
    }
  }, [separators]);

  useEffect(() => {
    onValidationChange(stepper2Valid);
  }, [separators, files]);

  return (
    <>
      <div className="text-left mb-3">
        <Title
          icon="ki-file-up"
          title={'Archivo Base'}
          subtitle={'Carga tu archivo y elige los números.'}
          type="info"
        />
      </div>
      <div className="row">
        <div className="d-flex align-items-center mb-5">
          <span className="symbol me-e6 symbol-label"></span>
          <div className="d-flex flex-column">
            <span className="fw-bolder fs-3 required">Separador y archivo</span>
            <span className="fs-6">
              Al seleccionar el separador y cargar tu archivo, podrás elegir la columna que contiene
              los números para enviar tus mensajes.
            </span>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-12 z-index-3">
            <div className="fv-row  mb-5 z-index-3">
              <label className="form-label required" htmlFor={'separatorInput'}>
                Tipo de separador
              </label>
              <Controller
                control={control}
                name="separator"
                rules={{ required: true }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <Select
                      id={'separatorInput'}
                      options={separatorList}
                      noOptionsMessage={({ inputValue }) =>
                        inputValue
                          ? t('general.no-options', { inputValue })
                          : t('general.no-options')
                      }
                      className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                      classNamePrefix="form-select-custom"
                      placeholder={'Selecciona el separador de tu archivo'}
                      onChange={(
                        selectedOption: {
                          value: string;
                          label: string;
                        } | null
                      ) => {
                        onChange(selectedOption ? selectedOption.value : null);
                      }}
                      value={
                        separatorList.find(
                          (option: { value: string; label: string }) => option.value === value
                        ) || null
                      }
                    />
                    <InvalidFeedback error={error} />
                  </>
                )}
              />
            </div>
          </div>
        </div>
        <div
          className={`col-md-12 fv-row mb-7 animate-fadeInUp animate-delay-1${loadingFile ? ' position-relative pe-none' : ''}`}
        >
          <Dropzone
            onChange={(newFiles) => updateFiles(newFiles)}
            accept=".zip,.txt,.csv,.text"
            maxFiles={1}
            className="dropzone border border-dashed border-primary rounded-4 mb-3"
          >
            <div className="dz-message needsclick d-flex justify-content-between align-items-center w-100 p-3">
              <div className={`d-flex align-items-center ${files.length > 0 ? 'pe-none' : ''}`}>
                <div className="me-3">
                  <Icon
                    icon={
                      loadingFile
                        ? 'ki-abstract-20'
                        : files.length === 0
                          ? 'ki-file-sheet'
                          : 'ki-file-added'
                    }
                    size="fs-3x"
                    type="primary"
                    className={loadingFile ? 'refresh-animate' : ''}
                  />
                </div>
                <div>
                  <h3 className="fs-6 fw-bold text-gray-900 mb-1">
                    {loadingFile ? (
                      'Estamos analizando tu archivo'
                    ) : files.length === 0 ? (
                      <>
                        Arrastra tu archivo o haz clic aquí.
                        <Tooltip
                          title="Te recomendamos que, si son más de 300,000 registros, comprimas tu archivo en formato ZIP, para procesar mejor información."
                          className="d-inline-block cursor-pointer"
                        >
                          <Icon
                            icon="ki-information-3"
                            size="fs-1"
                            className="ms-2"
                            type="danger"
                          />
                        </Tooltip>
                      </>
                    ) : (
                      'Tu archivo se analizó con éxito.'
                    )}
                  </h3>
                  <span className="fs-6 fw-semibold text-info d-block">
                    {loadingFile
                      ? uploadProgress === 100
                        ? 'Ya estamos terminado...'
                        : 'Espera un momento..'
                      : files.length === 0
                        ? '.csv .txt .zip o .text'
                        : files[0]?.name}
                  </span>
                </div>
              </div>
              {files.length > 0 && !loadingFile && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger"
                  onClick={removeFile}
                >
                  <u className="fw-bolder fs-5">Eliminar</u>
                </button>
              )}
              {loadingFile && (
                <span className="text-primary fw-bolder fs-2x">% {uploadProgress}</span>
              )}
            </div>
          </Dropzone>
        </div>
      </div>
      <div className="separator separator-dashed text-dark mb-10"></div>
      <div className="fv-row mb-1">
        <div className="d-flex flex-stack cursor-pointer mb-3">
          <div className="d-flex align-items-center mb-5">
            <div className="d-flex flex-column">
              <span className="fw-bolder fs-3">
                Omitir duplicados <span className="fs-7">({t('general.optional')})</span>
              </span>
              <span className="fs-6">
                Activa este switch si deseas evitar el envío de mensajes duplicados a destinatarios{' '}
                <br />
                repetidos en tu archivo base.
              </span>
            </div>
          </div>
          <span className="form-check form-check-custom form-check-solid">
            <Switch {...register('uniqueSend')} />
          </span>
        </div>
      </div>
    </>
  );
}

export default Stepper2;
